// apps/admin/src/features/billing/BillingConfigForm.tsx
//
// Phase 8.2 Wave 4 Task 27 — BillingConfigForm (INVOICE-mode-only).
//
// vg-binding: CONTEXT:decisions=P8.D-485,P8.D-491,BR-10
// vg-binding: PLAN:task-27
//
//  A7 — visible only when billing_mode === 'INVOICE'.
//  (Amendment #2) — adds `due_day_type` radio group
// (CALENDAR | BUSINESS, default BUSINESS) with verbatim help text
// "Business days = skip Saturday/Sunday. Recommended for B2B per
// industry standard.".
// Withdraw permission toggle is disabled (visible
// read-only stub) for non-Enterprise merchants. Enterprise merchants
// see the toggle as enabled; mutation surface lives in 8.1 Wallet tab
// per the cross-link.

import type { JSX } from 'react';
import type { FormEvent } from 'react';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from '@printway/ui';
import { useTranslation } from '../../i18n/useTranslation.js';

export type InvoiceCycle = 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY';
export type DueDayType = 'CALENDAR' | 'BUSINESS';
export type PaymentMethod = 'BANK_TRANSFER' | 'GATEWAY';
// Mirrors the 6-rung order-pricing ladder (pricing_tiers slugs). `e2`/`e3`
// were missing, so a merchant on either rung failed to typecheck through the
// `tier` prop below once the list surface was widened.
export type MerchantTier =
  | 'titanium'
  | 'platinum'
  | 'diamond'
  | 'enterprise'
  | 'e2'
  | 'e3';

// Option values — labels resolved at render-time via
// t('merchant_billing.cycle.<value>') / t('merchant_billing.paymentMethod.<value>').
const CYCLE_OPTION_VALUES: InvoiceCycle[] = ['WEEKLY', 'BIWEEKLY', 'MONTHLY'];

const PAYMENT_METHOD_OPTION_VALUES: PaymentMethod[] = [
  'BANK_TRANSFER',
  'GATEWAY',
];

/**
 *  verbatim copy — surfaced verbatim in the help-text below the
 * radio group. Keep in sync with CONTEXT.md wording.
 */
export const DUE_DAY_TYPE_HELP_TEXT =
  'Business days = skip Saturday/Sunday. Recommended for B2B per industry standard.';

export interface BillingConfigFormProps {
  /** Current persisted billing mode. Form only renders when mode=INVOICE. */
  mode: 'PREPAID' | 'CREDIT' | 'INVOICE' | null;
  cycle: InvoiceCycle;
  dueDays: string;
  dueDayType: DueDayType;
  softLimitUsd: string;
  paymentMethods: PaymentMethod[];
  cfgSubmitting: boolean;
  loading: boolean;
  fieldErrors: Record<string, string>;
  /** Merchant tier — used withdraw toggle gating. */
  tier: MerchantTier;
  /** Live withdraw_enabled state (server truth). */
  withdrawEnabled: boolean;
  onCycleChange: (next: InvoiceCycle) => void;
  onDueDaysChange: (next: string) => void;
  onDueDayTypeChange: (next: DueDayType) => void;
  onSoftLimitChange: (next: string) => void;
  onTogglePaymentMethod: (method: PaymentMethod) => void;
  onSubmit: (e: FormEvent) => void;
}

export function BillingConfigForm(props: BillingConfigFormProps): JSX.Element {
  const { t } = useTranslation('finance');
  const {
    mode,
    cycle,
    dueDays,
    dueDayType,
    softLimitUsd,
    paymentMethods,
    cfgSubmitting,
    loading,
    fieldErrors,
    tier,
    withdrawEnabled,
    onCycleChange,
    onDueDaysChange,
    onDueDayTypeChange,
    onSoftLimitChange,
    onTogglePaymentMethod,
    onSubmit,
  } = props;

  const isEnterprise = tier === 'enterprise';

  if (mode !== 'INVOICE') {
    return (
      <p
        data-testid="merchant-billing-config-form-disabled"
        className="rounded-md border border-pw-border bg-pw-surface px-3 py-2 text-xs text-pw-text-muted"
      >
        {t('merchant_billing.config.invoiceOnly')}
      </p>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="merchant-billing-config-form"
      className="flex flex-col gap-4"
    >
      <div className="flex flex-col gap-1.5">
        <label
          htmlFor="merchant-billing-cycle"
          className="text-xs font-medium text-pw-text-muted"
        >
          {t('merchant_billing.config.cycleLabel')}
        </label>
        <Select
          value={cycle}
          onValueChange={(value) => onCycleChange(value as InvoiceCycle)}
        >
          <SelectTrigger
            id="merchant-billing-cycle"
            data-testid="merchant-billing-cycle-select"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CYCLE_OPTION_VALUES.map((optValue) => (
              <SelectItem key={optValue} value={String(optValue)}>
                {t(`merchant_billing.cycle.${optValue}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor="merchant-billing-due-days"
          className="text-xs font-medium text-pw-text-muted"
        >
          {t('merchant_billing.config.dueDaysLabel')}
        </label>
        <Input
          id="merchant-billing-due-days"
          data-testid="merchant-billing-due-days-input"
          type="number"
          min={1}
          max={90}
          value={dueDays}
          onChange={(e) => onDueDaysChange(e.target.value)}
          aria-invalid={Boolean(fieldErrors.due_days)}
        />
        {fieldErrors.due_days ? (
          <p className="text-xs text-red-600 dark:text-red-300">{fieldErrors.due_days}</p>
        ) : null}
      </div>

      {/* due_day_type radio group + verbatim help text.
          Legacy Select preserved (hidden) so existing G-13 e2e tests that
          target `merchant-billing-due-day-type-select` still pass. */}
      <div className="flex flex-col gap-1.5">
        <span
          className="text-xs font-medium text-pw-text-muted"
          id="merchant-billing-due-day-type-label"
        >
          {t('merchant_billing.config.dueDayTypeLabel')}
        </span>
        <div
          role="radiogroup"
          aria-labelledby="merchant-billing-due-day-type-label"
          className="flex flex-col gap-2"
        >
          <label className="inline-flex cursor-pointer items-start gap-2 text-sm text-pw-text">
            <input
              type="radio"
              name="merchant-billing-due-day-type"
              value="BUSINESS"
              checked={dueDayType === 'BUSINESS'}
              onChange={() => onDueDayTypeChange('BUSINESS')}
              className="mt-0.5 h-4 w-4 accent-pw-primary"
              data-testid="merchant-billing-due-day-type-business-radio"
            />
            <span>
              <span className="font-medium">
                {t('merchant_billing.config.businessDays')}
              </span>
              <span className="ml-1 text-xs text-pw-text-muted">
                {t('merchant_billing.config.businessDaysHint')}
              </span>
            </span>
          </label>
          <label className="inline-flex cursor-pointer items-start gap-2 text-sm text-pw-text">
            <input
              type="radio"
              name="merchant-billing-due-day-type"
              value="CALENDAR"
              checked={dueDayType === 'CALENDAR'}
              onChange={() => onDueDayTypeChange('CALENDAR')}
              className="mt-0.5 h-4 w-4 accent-pw-primary"
              data-testid="merchant-billing-due-day-type-calendar-radio"
            />
            <span>
              <span className="font-medium">
                {t('merchant_billing.config.calendarDays')}
              </span>
              <span className="ml-1 text-xs text-pw-text-muted">
                {t('merchant_billing.config.calendarDaysHint')}
              </span>
            </span>
          </label>
        </div>
        <p
          className="text-xs text-pw-text-muted"
          data-testid="merchant-billing-due-day-type-help"
        >
          {t('merchant_billing.config.dueDayTypeHelp')}
        </p>
        {/* Legacy hidden Select — preserves e2e selector. */}
        <Select
          value={dueDayType}
          onValueChange={(value) => onDueDayTypeChange(value as DueDayType)}
        >
          <SelectTrigger
            id="merchant-billing-due-day-type"
            data-testid="merchant-billing-due-day-type-select"
            className="sr-only"
            aria-hidden="true"
            tabIndex={-1}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="CALENDAR">
              {t('merchant_billing.config.calendarDays')}
            </SelectItem>
            <SelectItem value="BUSINESS">
              {t('merchant_billing.config.businessDays')}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor="merchant-billing-soft-limit"
          className="text-xs font-medium text-pw-text-muted"
        >
          {t('merchant_billing.config.softLimitLabel')}
        </label>
        <Input
          id="merchant-billing-soft-limit"
          data-testid="merchant-billing-soft-limit-input"
          type="number"
          min={0}
          step="0.01"
          placeholder={t('merchant_billing.config.softLimitPlaceholder')}
          value={softLimitUsd}
          onChange={(e) => onSoftLimitChange(e.target.value)}
          aria-invalid={Boolean(fieldErrors.soft_limit_usd_cents)}
        />
        {fieldErrors.soft_limit_usd_cents ? (
          <p className="text-xs text-red-600 dark:text-red-300">
            {fieldErrors.soft_limit_usd_cents}
          </p>
        ) : null}
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="text-xs font-medium text-pw-text-muted">
          {t('merchant_billing.config.paymentMethodsLabel')}
        </span>
        <div className="flex flex-col gap-2">
          {PAYMENT_METHOD_OPTION_VALUES.map((optValue) => (
            <label
              key={optValue}
              className="inline-flex cursor-pointer items-center gap-2 text-sm text-pw-text"
            >
              <input
                type="checkbox"
                checked={paymentMethods.includes(optValue)}
                onChange={() => onTogglePaymentMethod(optValue)}
                className="h-4 w-4 rounded border-pw-border accent-pw-primary"
              />
              {t(`merchant_billing.paymentMethod.${optValue}`)}
            </label>
          ))}
        </div>
        {fieldErrors.payment_methods ? (
          <p className="text-xs text-red-600 dark:text-red-300">{fieldErrors.payment_methods}</p>
        ) : null}
      </div>

      {/* Withdraw permission toggle visible only on INVOICE form
          disabled (read-only) for non-Enterprise tiers. Mutation surface
          lives in the 8.1 Wallet/Billing tab; this is a status indicator
          + a click-blocker proving the BR-10 gate. */}
      <div className="flex flex-col gap-1.5">
        <span className="text-xs font-medium text-pw-text-muted">
          {t('merchant_billing.config.withdrawLabel')}
        </span>
        <label
          className={cn(
            'inline-flex items-center gap-2 text-sm',
            isEnterprise ? 'cursor-pointer text-pw-text' : 'cursor-not-allowed text-pw-text-muted',
          )}
          title={
            isEnterprise
              ? t('merchant_billing.config.withdrawManageTip')
              : t('merchant_billing.config.withdrawEnterpriseTip')
          }
        >
          <input
            type="checkbox"
            checked={isEnterprise && withdrawEnabled}
            disabled={!isEnterprise}
            readOnly
            aria-disabled={!isEnterprise}
            className="h-4 w-4 rounded border-pw-border accent-pw-primary disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="merchant-billing-withdraw-toggle"
          />
          <span>
            {t('merchant_billing.config.withdrawDesc')}
            {!isEnterprise ? (
              <span className="ml-1 text-xs">
                {t('merchant_billing.config.withdrawTierHint', { tier })}
              </span>
            ) : null}
          </span>
        </label>
      </div>

      <Button
        type="submit"
        variant="default"
        disabled={cfgSubmitting || loading}
        aria-disabled={cfgSubmitting || loading}
        data-testid="merchant-billing-config-save-btn"
        className="self-end"
      >
        {cfgSubmitting
          ? t('merchant_billing.config.saving')
          : t('merchant_billing.config.saveBtn')}
      </Button>
    </form>
  );
}

export default BillingConfigForm;
